import { Box } from "@mui/material";
import FormOptionCard, { FormOptionCardConfig } from "app/components/FormOptionCard";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectQuotationTyingEquipment } from "../../../redux/selector";
import { actions } from "../../../redux/slice";
import {
  AddOnItemFormValue,
  QuotationTyingEquipmentCardKey,
} from "../../../types";
import { useStyles as useAddOnStyles } from "../AddOn/styles";
import {
  QUOTATION_EQUIPMENT_TABLES,
  QUOTATION_FORM_I18N_PREFIX,
  QUOTATION_SECTION_KEYS,
} from "../data";
import { FormSection } from "../VehicleDetails/FormControls";
import QuotationEquipmentTable from "./QuotationEquipmentTable";
import QuotationInputCard from "./QuotationInputCard";
import LocksPanel from "./LocksPanel";
import SafetyVehiclePanel from "./SafetyVehiclePanel";
import { useStyles } from "./styles";

type TyingCardKey = Extract<
  QuotationTyingEquipmentCardKey,
  | "sideCableTensioner"
  | "strapTensioners"
  | "frontCableTensioner"
  | "recessedRings"
  | "apcRings"
  | "additionalTyingHooks"
>;
type StorageCardKey = Extract<
  QuotationTyingEquipmentCardKey,
  "polesBasket" | "waterJerrycan" | "palletJackMount"
>;

const P = `${QUOTATION_FORM_I18N_PREFIX}.TYING_EQUIPMENT`;
const tKey = (suffix: string) => `${P}.${suffix}`;

const TYING_CARDS: Record<TyingCardKey, FormOptionCardConfig> = {
  sideCableTensioner: {
    titleKey: tKey("CARDS.SIDE_CABLE_TENSIONER"), fields: ["quantity"],
    quantityLabelKey: tKey("FIELDS.QUANTITY"),
  },
  strapTensioners: {
    titleKey: tKey("CARDS.STRAP_TENSIONERS"), layout: "horizontal", fields: ["radio", "quantity"],
    radioLabelKey: tKey("FIELDS.TYPE"), quantityLabelKey: tKey("FIELDS.QUANTITY"),
    radioOptions: [{ value: "WIDE", labelKey: tKey("OPTIONS.WIDE") }],
  },
  frontCableTensioner: {
    titleKey: tKey("CARDS.FRONT_CABLE_TENSIONER"), fields: ["quantity"],
    quantityLabelKey: tKey("FIELDS.QUANTITY"),
  },
  recessedRings: {
    titleKey: tKey("CARDS.RECESSED_RINGS"), fields: ["quantity"],
    quantityLabelKey: tKey("FIELDS.QUANTITY_OPTION"),
  },
  apcRings: {
    titleKey: tKey("CARDS.APC_RINGS"), fields: ["quantity"],
    quantityLabelKey: tKey("FIELDS.QUANTITY_OPTION"),
  },
  additionalTyingHooks: {
    titleKey: tKey("CARDS.ADDITIONAL_TYING_HOOKS"), fields: ["quantity"],
    quantityLabelKey: tKey("FIELDS.QUANTITY_OPTION"),
  },
};

const STORAGE_CARDS: Record<StorageCardKey, FormOptionCardConfig> = {
  polesBasket: {
    titleKey: tKey("CARDS.POLES_BASKET"), fields: ["quantity"],
    quantityLabelKey: tKey("FIELDS.QUANTITY"),
  },
  waterJerrycan: {
    titleKey: tKey("CARDS.WATER_JERRYCAN"), fields: ["quantity"],
    quantityLabelKey: tKey("FIELDS.QUANTITY"),
  },
  palletJackMount: {
    titleKey: tKey("CARDS.PALLET_JACK_MOUNT"), layout: "horizontal", fields: ["radio", "note"],
    radioLabelKey: tKey("FIELDS.TYPE"), notePlaceholderKey: tKey("FIELDS.NOTE"),
    radioOptions: [
      { value: "FROM_SIDE", labelKey: tKey("OPTIONS.FROM_SIDE") },
      { value: "REGULAR", labelKey: tKey("OPTIONS.REGULAR") },
    ],
  },
};

const TyingEquipmentPanel = () => {
  const dispatch = useDispatch();
  const tyingEquipment = useSelector(selectQuotationTyingEquipment);
  const styles = useStyles();
  const addOnStyles = useAddOnStyles();
  if (!tyingEquipment) return null;

  const labels = {
    note: tKey("FIELDS.NOTE"), quantity: tKey("FIELDS.QUANTITY"),
    size: tKey("FIELDS.QUANTITY"), location: tKey("FIELDS.QUANTITY_OPTION"), itemType: tKey("FIELDS.TYPE"),
  };
  const update = (key: QuotationTyingEquipmentCardKey, value: Partial<AddOnItemFormValue>) =>
    dispatch(actions.updateQuotationTyingEquipment({ key, value }));
  const renderCard = <K extends QuotationTyingEquipmentCardKey>(
    key: K,
    configs: Record<K, FormOptionCardConfig>
  ) => (
    <FormOptionCard key={key} config={configs[key]} value={tyingEquipment[key]} labels={labels}
      onChange={(v) => update(key, v)} />
  );

  return (
    <QuotationInputCard titleKey={QUOTATION_SECTION_KEYS.EQUIPMENT}>
      <FormSection titleKey={tKey("SECTIONS.TYING")} hideTopBorder titleClassName={styles.sectionTitle}>
        <Box className={addOnStyles.sectionContent}>
          <Box className={`${addOnStyles.cardRow} ${addOnStyles.cardRow2}`}>
            {renderCard("sideCableTensioner", TYING_CARDS)}
            {renderCard("strapTensioners", TYING_CARDS)}
          </Box>
          <Box className={styles.tyingGrid4}>
            {renderCard("frontCableTensioner", TYING_CARDS)}
            {renderCard("recessedRings", TYING_CARDS)}
            {renderCard("apcRings", TYING_CARDS)}
            {renderCard("additionalTyingHooks", TYING_CARDS)}
          </Box>
        </Box>
      </FormSection>

      <FormSection titleKey={tKey("SECTIONS.STORAGE")} titleClassName={styles.sectionTitle}>
        <Box className={addOnStyles.tablesWrapper}>
          {QUOTATION_EQUIPMENT_TABLES.map((table) => (
            <QuotationEquipmentTable
              key={table.tableKey}
              titleKey={table.titleKey}
              columns={table.columns}
              rows={tyingEquipment[table.tableKey]}
              variant={table.variant}
              onAddRow={() => dispatch(actions.addQuotationEquipmentTableRow(table.tableKey))}
            />
          ))}
        </Box>
        <Box className={`${addOnStyles.cardRow} ${addOnStyles.cardRow3}`}>
          {renderCard("polesBasket", STORAGE_CARDS)}
          {renderCard("waterJerrycan", STORAGE_CARDS)}
          {renderCard("palletJackMount", STORAGE_CARDS)}
        </Box>
      </FormSection>
      <SafetyVehiclePanel />
      <LocksPanel />
    </QuotationInputCard>
  );
};

export default TyingEquipmentPanel;
